import type { FC } from "react";
import MuiRadioGroup from "@mui/material/RadioGroup";
import { Radio } from "@privacy/ui";
import { useMutation } from "@tanstack/react-query";
import { useController, useFormContext } from "react-hook-form";
import type { InputProps } from "../../DynamicInput";
import type { Option as OptionType } from "../../types";
import { useDynamicFormState } from "../../DynamicFormProvider";
import { ErrorMessage } from "../../inlineForm/ErrorMessage";
import { isAnswerRequestEmpty } from "../../shared/isAnswerRequestEmpty";
import { Option } from "./Option";

type RadioGroupProps = InputProps<string> & { options: OptionType[] };
export const RadioGroup: FC<RadioGroupProps> = ({ questionCard, readonly, options, onSave }) => {
  const { control } = useFormContext();
  const { field } = useController({ control, name: questionCard.id });
  const { mandatoryQuestionErrorMessage } = useDynamicFormState();

  const saveMutation = useMutation({
    mutationFn: async (value: string | null) => {
      field.onChange(value);
      return onSave(value);
    },
  });

  return (
    <>
      <MuiRadioGroup value={field.value ?? null}>
        {options.map(option => (
          <Option
            disabled={readonly}
            key={option.value}
            checked={field.value === option.value}
            onClick={() => saveMutation.mutate(field.value === option.value ? null : option.value)}
            option={option}
            Control={Radio}
          />
        ))}
      </MuiRadioGroup>
      {!saveMutation.isPending && saveMutation.isError && <ErrorMessage />}
      {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(field.value) && (
        <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
      )}
    </>
  );
};
